import React from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';
import styles from './App.module.css';
import AppHeader from './components/AppHeader/AppHeader';
import Footer from './components/Footer';
import PrivateRoute from './components/PrivateRoute';
import RoleBasedRoute from './components/RoleBasedRoute';
import RoleDashboardRedirect from './components/RoleDashboardRedirect';
import { AuthProvider, useIsAuthenticated, useUser } from './contexts/AuthContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import './index.css';
import AdminDashboard from './pages/AdminDashboard';
import AdminEvaluationSummary from './pages/AdminEvaluationSummary';
import AdminHome from './pages/AdminHome';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminUserCreate from './pages/AdminUserCreate';
import AdminUserDetail from './pages/AdminUserDetail';
import AdminUsersList from './pages/AdminUsersList';
import ApplicantDashboard from './pages/ApplicantDashboard';
import ApplicationEvaluations from './pages/ApplicationEvaluations';
import CasCallback from './pages/CasCallback';
import DynamicApplication from './pages/DynamicApplication';
import EvaluationInterface from './pages/EvaluationInterface';
import EvaluatorDashboard from './pages/EvaluatorDashboard';
import LoginPage from './pages/LoginPage';
import PasswordSetup from './pages/PasswordSetup';
import EvaluatorInvitation from './pages/EvaluatorInvitation';
import ResetPassword from './pages/ResetPassword';
import StatPrototype from './pages/StatPrototype';

const LanguageWarningTopbar: React.FC = () => {
  const { showWarning, dismissWarning } = useLanguage();
  const user = useUser();
  const isAuthenticated = useIsAuthenticated();
  if (!showWarning || !isAuthenticated || user?.role !== 'researcher') return null;
  return (
    <div
      role="alert"
      style={{
        background: 'var(--color-bg-fill-soft-warning)',
        borderBottom: '1px solid var(--color-warning-lighter)',
        padding: 'var(--spacing-12) var(--spacing-24)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--spacing-16)',
      }}
    >
      <p style={{ margin: 0, color: 'var(--color-text-warning)', fontSize: '0.875rem', flex: 1 }}>
        <strong>Atención:</strong> Estás visualizando el formulario en español como ayuda de lectura,
        pero las respuestas de los campos de texto y los documentos adjuntos deben redactarse{' '}
        <strong>en inglés</strong> para ser admitidos a evaluación.
      </p>
      <button
        onClick={dismissWarning}
        style={{
          background: 'none',
          border: '1px solid var(--color-text-warning)',
          borderRadius: 'var(--border-radius-4)',
          color: 'var(--color-text-warning)',
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: '0.8rem',
          fontWeight: 600,
          padding: 'var(--spacing-4) var(--spacing-12)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Entendido
      </button>
    </div>
  );
};

// Main app routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/stat-prototype" element={<StatPrototype />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/cas-callback" element={<CasCallback />} />
      <Route path="/setup-password" element={<PasswordSetup />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/evaluator/invitation/:token" element={<EvaluatorInvitation />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <RoleDashboardRedirect />
          </PrivateRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <RoleDashboardRedirect />
          </PrivateRoute>
        }
      />

      {/* Researcher routes */}
      <Route
        path="/researcher/dashboard"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['researcher', 'admin']}>
              <ApplicantDashboard />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/researcher/applications/:id"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['researcher', 'admin']}>
              <DynamicApplication />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/researcher/applications/:applicationId/evaluations"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['researcher', 'admin']}>
              <ApplicationEvaluations />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/applications/:applicationId/evaluations"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['researcher', 'admin']}>
              <ApplicationEvaluations />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />

      {/* Evaluator routes */}
      <Route
        path="/evaluator/dashboard"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['evaluator']}>
              <EvaluatorDashboard />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/evaluator/evaluate/:id"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['evaluator']}>
              <EvaluationInterface />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/evaluator/applications/:id"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['evaluator']}>
              <DynamicApplication />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin/dashboard"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminHome />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/dashboard/:templateId"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminDashboard />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminUsersList />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users/create"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminUserCreate />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminUserDetail />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/applications/:id"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <DynamicApplication isAdmin={true} />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/application/:id"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <DynamicApplication isAdmin={true} />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/evaluation/:evaluationId"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['admin']}>
              <AdminEvaluationSummary />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />

      {/* Legacy routes - keep for backward compatibility */}
      <Route
        path="/applicant/dashboard"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['researcher']}>
              <ApplicantDashboard />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
      <Route
        path="/dynamic-application/:id"
        element={
          <PrivateRoute>
            <RoleBasedRoute allowedRoles={['researcher']}>
              <DynamicApplication />
            </RoleBasedRoute>
          </PrivateRoute>
        }
      />
    </Routes>
  );
};

function App() {
  // useeffect
  React.useEffect(() => {
    // display #uc-dev-footer
    const devFooter = document.getElementById('uc-dev-footer');
    if (devFooter) {
      devFooter.style.display = 'block';
    }
  }, []);
  return (
    <Router>
      <AuthProvider>
        <LanguageProvider>
          <div className={styles.appContainer}>
            <AppHeader />
            <LanguageWarningTopbar />
            <main className={styles.mainContent}>
              <AppRoutes />
            </main>
            <Footer />
          </div>
        </LanguageProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
